<template>
  <div class="api-test-container">
    <div class="header">
      <h1>API测试页面</h1>
    </div>
    
    <div class="content">
      <div class="actions">
        <el-row :gutter="20">
          <el-col :span="8">
            <div class="action-card">
              <h3>1. 用户登录</h3>
              <p>使用admin/admin登录验证</p>
              <div class="user-info" v-if="isLoggedIn">
                <p><strong>当前登录用户:</strong> {{ userInfo.username }}</p>
                <p><strong>角色:</strong> {{ userInfo.role }}</p>
                <p><strong>单位:</strong> {{ userInfo.unitName }}</p>
              </div>
              <el-button type="primary" @click="handleLogin" :loading="loadingLogin">登录</el-button>
            </div>
          </el-col>
          
          <el-col :span="8">
            <div class="action-card">
              <h3>2. 用户登出</h3>
              <p>退出当前登录</p>
              <el-button type="danger" @click="handleLogout" :loading="loadingLogout" :disabled="!isLoggedIn">登出</el-button>
            </div>
          </el-col>
          
          <el-col :span="8">
            <div class="action-card">
              <h3>3. 单位分页查询</h3>
              <p>无条件分页显示单位信息</p>
              <el-button type="success" @click="handleQueryUnits" :loading="loadingUnits">查询单位</el-button>
            </div>
          </el-col>
        </el-row>
      </div>
      
      <div class="result-section">
        <div class="section-header">
          <h2>单位列表</h2>
          <el-pagination
            v-if="unitData.total > 0"
            @size-change="handleSizeChange"
            @current-change="handleCurrentChange"
            :current-page="pagination.page"
            :page-sizes="[5, 10, 20, 50]"
            :page-size="pagination.pageSize"
            layout="total, sizes, prev, pager, next"
            :total="unitData.total">
          </el-pagination>
        </div>
        
        <el-table
          v-if="unitData.list.length > 0"
          :data="unitData.list"
          border
          style="width: 100%">
          <el-table-column prop="id" label="ID" width="80"></el-table-column>
          <el-table-column prop="unit_name" label="单位名称" min-width="180"></el-table-column>
          <el-table-column prop="contact_person" label="联系人" min-width="120"></el-table-column>
          <el-table-column prop="phone" label="联系电话" min-width="150"></el-table-column>
          <el-table-column prop="address" label="单位地址" min-width="200"></el-table-column>
          <el-table-column prop="tax_id" label="税号" min-width="180"></el-table-column>
        </el-table>
        
        <div v-else-if="loadingUnits" class="no-data">
          <i class="el-icon-loading"></i>
          <p>加载中...</p>
        </div>
        
        <div v-else class="no-data">
          <i class="el-icon-info"></i>
          <p>暂无数据，请点击"查询单位"按钮获取数据</p>
        </div>
      </div>
      
      <div class="debug-panel">
        <div class="section-header">
          <h2>调试信息</h2>
          <el-button size="small" type="text" @click="clearDebugInfo">清空</el-button>
        </div>
        <pre class="debug-info">{{ debugInfo }}</pre>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/api'
import { mapGetters } from 'vuex'
import { setUserInfo, setToken, removeUserInfo, removeToken } from '@/utils/auth'

export default {
  name: 'ApiTest',
  data() {
    return {
      // 加载状态
      loadingLogin: false,
      loadingLogout: false,
      loadingUnits: false,
      
      // 分页参数
      pagination: {
        page: 1,
        pageSize: 10
      },
      
      // 单位数据
      unitData: {
        list: [],
        total: 0
      },
      
      // 调试信息
      debugInfo: '',
      
      // 当前操作类型
      currentAction: ''
    }
  },
  computed: {
    ...mapGetters(['userInfo', 'token', 'isLoggedIn'])
  },
  methods: {
    // 登录处理
    handleLogin() {
      this.currentAction = 'login'
      this.loadingLogin = true
      
      const loginData = {
        username: 'admin',
        password: 'admin'
      }
      
      this.addDebugInfo('正在执行登录操作...', loginData)
      
      api.user.login(loginData)
        .then(response => {
          this.addDebugInfo('登录API响应:', response.data)
          
          if (response.data && response.data.success) {
            const userData = response.data.data
            
            // 构建用户信息对象
            const userInfo = {
              userId: userData.id,
              username: userData.username,
              role: userData.role,
              roleType: userData.role_type,
              unitId: userData.unit_id,
              unitName: userData.unit_name,
              unitType: userData.unit_type,
              menus: userData.menus
            }
            
            // 存储到Vuex
            this.$store.dispatch('setToken', userData.token)
            this.$store.dispatch('setUserInfo', userInfo)
            
            // 存储到localStorage
            setToken(userData.token)
            setUserInfo(userInfo)
            
            this.addDebugInfo('登录成功，用户信息:', userInfo)
            this.$message.success('登录成功')
          } else {
            this.addDebugInfo('登录失败:', response.data.message)
            this.$message.error(response.data.message || '登录失败')
          }
        })
        .catch(error => {
          this.addDebugInfo('登录出错:', error)
          this.$message.error('登录失败: ' + (error.message || '未知错误'))
        })
        .finally(() => {
          this.loadingLogin = false
        })
    },
    
    // 登出处理
    handleLogout() {
      this.currentAction = 'logout'
      this.loadingLogout = true
      
      this.addDebugInfo('正在执行登出操作...')
      
      api.user.logout()
        .then(response => {
          this.addDebugInfo('登出API响应:', response.data)
          
          // 清除Vuex状态
          this.$store.dispatch('logout')
          
          // 清除localStorage中的认证信息
          removeToken()
          removeUserInfo()
          
          this.addDebugInfo('登出成功')
          this.$message.success('已登出')
        })
        .catch(error => {
          this.addDebugInfo('登出出错:', error)
          this.$message.error('登出失败: ' + (error.message || '未知错误'))
          
          // 即使API失败，也清除本地存储
          this.$store.dispatch('logout')
          removeToken()
          removeUserInfo()
        })
        .finally(() => {
          this.loadingLogout = false
        })
    },
    
    // 查询单位信息
    handleQueryUnits() {
      this.currentAction = 'queryUnits'
      this.loadingUnits = true
      
      const params = {
        page: this.pagination.page,
        pageSize: this.pagination.pageSize
      }
      
      this.addDebugInfo('正在查询单位信息...', params)
      
      api.unit.query_unit_pagination(params)
        .then(response => {
          this.addDebugInfo('查询单位API响应:', response.data)
          
          if (response.data && response.data.success) {
            const data = response.data.data
            this.unitData = {
              list: data.list || [],
              total: (data.pagination && data.pagination.totalItems) || 0
            }
            
            this.addDebugInfo('查询成功，获取到 ' + this.unitData.list.length + ' 条记录')
            this.$message.success('查询成功')
          } else {
            this.addDebugInfo('查询失败:', response.data.message)
            this.$message.error(response.data.message || '查询单位信息失败')
          }
        })
        .catch(error => {
          this.addDebugInfo('查询单位出错:', error)
          this.$message.error('查询单位信息失败: ' + (error.message || '未知错误'))
        })
        .finally(() => {
          this.loadingUnits = false
        })
    },
    
    // 处理分页大小变化
    handleSizeChange(val) {
      this.pagination.pageSize = val
      this.handleQueryUnits()
    },
    
    // 处理页码变化
    handleCurrentChange(val) {
      this.pagination.page = val
      this.handleQueryUnits()
    },
    
    // 添加调试信息
    addDebugInfo(title, data = null) {
      const timestamp = new Date().toLocaleTimeString()
      let message = `[${timestamp}] ${title}\n`
      
      if (data) {
        if (typeof data === 'object') {
          message += JSON.stringify(data, null, 2) + '\n'
        } else {
          message += data + '\n'
        }
      }
      
      this.debugInfo = message + '\n' + this.debugInfo
    },
    
    // 清空调试信息
    clearDebugInfo() {
      this.debugInfo = ''
    }
  }
}
</script>

<style lang="scss" scoped>
.api-test-container {
  padding: 20px;
  background-color: #f5f7fa;
  min-height: 100vh;
  
  .header {
    margin-bottom: 30px;
    
    h1 {
      font-size: 24px;
      color: #303133;
      border-left: 4px solid #409EFF;
      padding-left: 10px;
    }
  }
  
  .content {
    background-color: #fff;
    border-radius: 4px;
    box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
    padding: 20px;
  }
  
  .actions {
    margin-bottom: 30px;
  }
  
  .action-card {
    background-color: #f8f9fa;
    border-radius: 4px;
    padding: 20px;
    height: 200px;
    display: flex;
    flex-direction: column;
    
    h3 {
      font-size: 18px;
      color: #303133;
      margin-top: 0;
      margin-bottom: 15px;
    }
    
    p {
      color: #606266;
      margin-bottom: 15px;
    }
    
    .user-info {
      background-color: #ecf5ff;
      border-radius: 4px;
      padding: 10px;
      margin-bottom: 15px;
      
      p {
        margin: 5px 0;
        font-size: 14px;
      }
    }
    
    .el-button {
      margin-top: auto;
    }
  }
  
  .result-section,
  .debug-panel {
    margin-top: 30px;
    padding: 20px;
    background-color: #f8f9fa;
    border-radius: 4px;
    
    .section-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 20px;
      
      h2 {
        font-size: 18px;
        color: #303133;
        margin: 0;
      }
    }
  }
  
  .no-data {
    text-align: center;
    padding: 30px 0;
    color: #909399;
    
    i {
      font-size: 40px;
      margin-bottom: 10px;
    }
  }
  
  .debug-info {
    background-color: #2d2d2d;
    color: #ffffff;
    border-radius: 4px;
    padding: 15px;
    font-family: 'Courier New', Courier, monospace;
    font-size: 14px;
    overflow-x: auto;
    white-space: pre-wrap;
    max-height: 300px;
    overflow-y: auto;
  }
}
</style> 